"use client";

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { default as NextLink } from "next/link";
import { useNotification } from "../Notifications";
import { Link } from "@zapier/design-system";
import {
  createUserTemplateBookmark,
  deleteUserTemplateBookmark,
  getUserBookmarkedTemplateIds,
} from "src/lib/actions";

type TemplateBookmarkProviderProps = {
  bookmarkedTemplates: string[];
  createUserTemplateBookmark: (
    templateId: string,
  ) => Promise<
    { user_id: number; template_id: string; created_at: string } | undefined
  >;

  deleteUserTemplateBookmark: (templateId: string) => Promise<void>;
  templateBookmarkChanging: string | null;
};

const TemplateBookmarkContext = createContext<TemplateBookmarkProviderProps>(
  {} as TemplateBookmarkProviderProps,
);

export const useTemplateBookmark = () => {
  return useContext(TemplateBookmarkContext);
};

export const TemplateBookmarkProvider = ({
  children,
  bookmarkedTemplates,
}: TemplateBookmarkProviderProps & { children: ReactNode }) => {
  const [userBookmarkedTemplates, setUserBookmarkedTemplates] =
    useState<string[]>(bookmarkedTemplates);
  const [templateBookmarkChanging, setTemplateBookmarkChanging] = useState<
    string | null
  >(null);

  const { createNotification, clearNotification } = useNotification();

  useEffect(() => {
    const fetchUserBookmarkedTemplates = async () => {
      const templateIds = await getUserBookmarkedTemplateIds();
      setUserBookmarkedTemplates(templateIds);
    };

    fetchUserBookmarkedTemplates();
  }, []);

  const saveUserBookmarkedTemplate = async (templateId: string) => {
    setTemplateBookmarkChanging(templateId);
    try {
      const data = await createUserTemplateBookmark(templateId);
      setUserBookmarkedTemplates([...userBookmarkedTemplates, templateId]);

      createNotification({
        id: "template-bookmark",
        onDismiss: () => {
          clearNotification("template-bookmark");
        },
        content: (
          <>
            <strong>Saved. </strong>
            <Link href="/app/templates/saved" component={NextLink}>
              View saved templates
            </Link>
          </>
        ),
        variant: "toast",
      });
      return data;
    } catch (error) {
      createNotification({
        id: "template-bookmark",
        onDismiss: () => {
          clearNotification("template-bookmark");
        },
        content: <p>Error saving template.</p>,
        variant: "toast",
        status: "error",
      });
    } finally {
      setTemplateBookmarkChanging(null);
    }
  };

  const removeUserBookmarkedTemplate = async (templateId: string) => {
    setTemplateBookmarkChanging(templateId);
    try {
      await deleteUserTemplateBookmark(templateId);
      setUserBookmarkedTemplates(
        userBookmarkedTemplates.filter((id) => id !== templateId),
      );
    } catch (error) {
      createNotification({
        id: "template-bookmark",
        onDismiss: () => {
          clearNotification("template-bookmark");
        },
        content: <p>Error removing template bookmark.</p>,
        variant: "toast",
        status: "error",
      });
    } finally {
      setTemplateBookmarkChanging(null);
    }
  };

  return (
    <TemplateBookmarkContext.Provider
      value={{
        bookmarkedTemplates: userBookmarkedTemplates,
        createUserTemplateBookmark: saveUserBookmarkedTemplate,
        deleteUserTemplateBookmark: removeUserBookmarkedTemplate,
        templateBookmarkChanging,
      }}
    >
      {children}
    </TemplateBookmarkContext.Provider>
  );
};
